// Acceptable Use Policy for SubsWall (Dr. Gotthilf LLC, Wyoming).
// Incorporated into the Terms of Service by reference.

export default function AcceptableUse(): JSX.Element {
  return (
    <article className="legal-page">
      <h1>Acceptable Use Policy</h1>
      <p className="legal-meta">
        Effective date: 2026-05-30 · Last updated: 2026-05-30
      </p>

      <p>
        <em>
          This document is the publicly-posted acceptable use policy for
          SubsWall. It is not legal advice. This Policy is incorporated
          into our <a href="/terms">Terms of Service</a> by reference, and
          alignment with Stripe's restricted businesses policy is a
          condition of using the Platform.
        </em>
      </p>

      <p>
        The SubsWall Platform is operated by <strong>Dr. Gotthilf LLC</strong>
        {" "}(Sheridan, Wyoming, USA). This Acceptable Use Policy
        (<strong>"AUP"</strong>) describes content and behavior that is not
        permitted on the Platform. Violations may result in immediate
        suspension or termination, donation freezes, refunds to Donors, and
        reporting to Stripe and to law enforcement.
      </p>

      <h2>1. Prohibited content</h2>
      <ul>
        <li>
          <strong>Gambling, lotteries, and wagering.</strong> Sports betting,
          casino-style games, lotteries, raffles, sweepstakes with paid
          entry, fantasy sports for value, or any activity where Donations
          are tied to a chance to win a prize.
        </li>
        <li>
          <strong>Adult / NSFW content.</strong> Pornography, sexually
          explicit content, escort and companionship services, adult
          subscription content, or sexual services of any kind.
        </li>
        <li>
          <strong>Weapons and dangerous goods.</strong> Firearms, ammunition,
          gun parts, silencers, explosives, fireworks, knives marketed for
          combat, body armor regulated in your jurisdiction.
        </li>
        <li>
          <strong>Drugs and drug paraphernalia.</strong> Illegal drugs,
          prescription drugs without a license, cannabis and CBD where
          federally restricted in the United States, e-cigarettes and vapes
          where regulated, anabolic steroids, smart drugs.
        </li>
        <li>
          <strong>Hate, harassment, and violence.</strong> Content promoting
          hatred against a person or group based on race, ethnicity, national
          origin, religion, disability, gender, sexual orientation, gender
          identity, age, or any protected characteristic. Threats, incitement
          to violence, doxxing, stalking, or coordinated harassment.
        </li>
        <li>
          <strong>Child safety violations.</strong> Any content that
          sexualizes, exploits, or endangers minors. We report to NCMEC and
          to law enforcement without notice to the offending account.
        </li>
        <li>
          <strong>Terrorism and extremism.</strong> Support for or financing
          of terrorist organizations or designated extremist groups.
        </li>
        <li>
          <strong>Illegal activity.</strong> Any content or activity that is
          illegal in the Creator's or Donor's jurisdiction or in the United
          States, including unlicensed financial services, unregistered
          securities, illegal lotteries, or trafficking.
        </li>
        <li>
          <strong>Intellectual-property infringement.</strong> Copyrighted
          works used without a license, counterfeit goods, unlicensed
          branded merchandise.
        </li>
      </ul>

      <h2>2. Prohibited behavior</h2>
      <ul>
        <li>
          <strong>Chargeback abuse.</strong> Initiating chargebacks instead
          of contacting <a href="mailto:support@subswall.com">support@subswall.com</a>{" "}
          for a refund. Repeated chargeback claims that turn out to be
          unfounded.
        </li>
        <li>
          <strong>Fraud.</strong> Using a payment method you are not
          authorized to use; coordinating with others to send Donations and
          then dispute them; testing stolen card numbers on the Platform;
          impersonating another Creator.
        </li>
        <li>
          <strong>Self-funding and metric inflation.</strong> Donating to
          yourself, or coordinating others to donate, in order to inflate
          rankings, evade fees, launder funds, or qualify for any incentive.
        </li>
        <li>
          <strong>Multi-accounting.</strong> Creating multiple Creator
          accounts to evade suspension, limits, or sanctions controls.
        </li>
        <li>
          <strong>Money laundering and sanctions evasion.</strong> Using the
          Platform to obscure the source of funds, to move funds for
          sanctioned persons or jurisdictions, or to convert proceeds of
          crime.
        </li>
        <li>
          <strong>Security violations.</strong> Probing, scanning, or
          testing the Platform's security without our written permission;
          reverse engineering; abusing the API beyond documented rate
          limits; uploading malware; attempting unauthorized access.
        </li>
        <li>
          <strong>Spam and platform abuse.</strong> Mass-creating accounts;
          using the overlay or Donation messages to spam links, scam
          messages, or unauthorized advertising.
        </li>
      </ul>

      <h2>3. Enforcement</h2>
      <p>
        If we determine, in our reasonable judgment, that a Creator or Donor
        has violated this AUP, we may take any combination of the following
        actions, with or without notice:
      </p>
      <ul>
        <li>Immediate suspension or termination of the account.</li>
        <li>Freeze of pending or scheduled Donation payouts pending review.</li>
        <li>Reversal of recent Donations and refund to the originating
          payment methods.</li>
        <li>Notification to Stripe, which may result in independent
          suspension of the Creator's Connected Account.</li>
        <li>Notification to law enforcement and regulators where the
          conduct involves criminal activity, child safety, or sanctions.</li>
        <li>Retention of records relating to the violation for as long as
          we reasonably need them for investigation, dispute resolution,
          and legal-defense purposes.</li>
      </ul>

      <h2>4. Reporting a violation</h2>
      <p>
        If you see content or behavior on the Platform that you believe
        violates this AUP, email{" "}
        <a href="mailto:abuse@subswall.com">abuse@subswall.com</a> with
        a link to the Creator profile or Donation in question and a short
        description of the issue. We review reports within two (2) business
        days. For urgent child-safety issues, include the word{" "}
        <strong>URGENT-CHILD-SAFETY</strong> in the subject line.
      </p>

      <h2>5. Updates</h2>
      <p>
        We may update this AUP at any time to keep pace with new abuse
        patterns, network rules, or Stripe policy. Material changes will be
        posted with a new effective date.
      </p>

      <h2>6. Contact</h2>
      <p>
        Abuse: <a href="mailto:abuse@subswall.com">abuse@subswall.com</a><br />
        Support: <a href="mailto:support@subswall.com">support@subswall.com</a><br />
        Mail: Dr. Gotthilf LLC, 30 N Gould St Ste R, Sheridan, WY 82801, USA
      </p>
    </article>
  );
}
